import React, { useEffect, useRef, useState } from 'react';
import { SelectOption } from './types/select-options';

interface TableDataEditorProps {
  value: any;
  columnType: string;
  columnOptions?: Record<string, any>;
  onChange: (newValue: any) => void;
  onBlur: (finalValue?: any) => void;
  onKeyDown: (e: React.KeyboardEvent, currentValue?: any) => void;
}

export function TableDataEditor({
  value,
  columnType,
  columnOptions = {},
  onBlur,
  onKeyDown,
}: TableDataEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Format initial value for editing
  const formatValueForEditing = (val: any) => {
    if (val === null || val === undefined) return '';

    switch (columnType) {
      case 'date':
        return val instanceof Date
          ? val.toISOString().split('T')[0] // YYYY-MM-DD format
          : '';
      case 'boolean':
        return val ? 'true' : 'false';
      default:
        return String(val);
    }
  };

  // Use local state to track the input value during editing
  const [localValue, setLocalValue] = useState(formatValueForEditing(value));

  // Auto-focus the input when the editor opens
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();

      // setSelectionRange only works on text, search, url, tel, and password inputs
      if (columnType === 'text' || columnType === 'select') {
        // Move cursor to end of text
        const length = inputRef.current.value.length;
        try {
          inputRef.current.setSelectionRange(length, length);
        } catch {
          // Silently handle the error for unsupported input types
        }
      }
    }
  }, [columnType]);

  // Custom blur handler to save changes
  const handleBlur = () => {
    console.log('🔁 BLUR - saving value:', localValue);
    // Save the final value when leaving the input - make sure to process it properly
    let processedValue;
    switch (columnType) {
      case 'number':
        processedValue = localValue === '' ? '' : parseFloat(localValue);
        break;
      case 'boolean':
        processedValue = localValue === 'true';
        break;
      default:
        processedValue = localValue;
    }
    onBlur(processedValue); // Pass the processed value back to parent
  };

  // Custom key handler to save changes on Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      console.log('🔁 ENTER - saving value:', localValue);
      // Process the value before sending it to parent
      let processedValue;
      switch (columnType) {
        case 'number':
          processedValue = localValue === '' ? '' : parseFloat(localValue);
          break;
        case 'boolean':
          processedValue = localValue === 'true';
          break;
        default:
          processedValue = localValue;
      }
      // Call parent with processed value
      onKeyDown(e, processedValue);
    } else if (e.key === 'Escape') {
      // Reset to original value
      setLocalValue(formatValueForEditing(value));
      onKeyDown(e, undefined); // No value change
    } else {
      // For other keys, don't save yet
      onKeyDown(e, undefined);
    }
  };

  // Update local state but don't trigger onChange yet
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setLocalValue(e.target.value);
  };

  // Common class for all input types to ensure consistent sizing
  const commonClassNames =
    'w-full h-full min-h-[40px] p-2 border border-blue-400 bg-white absolute inset-0';

  // Special styles for different column types
  const getTypeSpecificClasses = () => {
    if (columnType === 'number') {
      return '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
    }
    return '';
  };

  // Render different input based on column type
  const renderEditor = () => {
    switch (columnType) {
      case 'number':
        return (
          <input
            ref={inputRef}
            type="number"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`${commonClassNames} ${getTypeSpecificClasses()}`}
            step="any"
          />
        );

      case 'date':
        return (
          <input
            ref={inputRef}
            type="date"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`${commonClassNames} ${getTypeSpecificClasses()}`}
          />
        );

      case 'boolean':
        return (
          <select
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`${commonClassNames} ${getTypeSpecificClasses()}`}
            autoFocus
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );

      case 'select':
        if (columnOptions.options && Array.isArray(columnOptions.options)) {
          const options = columnOptions.options as SelectOption[];

          return (
            <select
              value={localValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className={`${commonClassNames} ${getTypeSpecificClasses()}`}
              autoFocus
            >
              <option value="">Select...</option>
              {options.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        }
        // Fallback to text input if options aren't properly defined
        return (
          <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`${commonClassNames} ${getTypeSpecificClasses()}`}
          />
        );

      default: // text
        return (
          <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`${commonClassNames} ${getTypeSpecificClasses()}`}
          />
        );
    }
  };

  return <div className="h-full w-full absolute inset-0">{renderEditor()}</div>;
}
